'use client'

import { useEffect, useMemo, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Check } from 'lucide-react'
import { OnboardingLayout } from '@/features/onboarding/components/onboarding-layout'
import { formatTime } from '@/lib/format/date'
import {
  submissionSuccessfulSchema,
  type SubmissionSuccessfulFormValues,
} from '@/features/onboarding/schemas/submission-successful.schema'
import { useOnboardingStore } from '@/features/onboarding/store/onboarding-store'
import {
  useSubmitPublicCase,
  useSubmitPublicBuildPortfolioCase,
} from '@/features/onboarding/hooks/use-onboarding'
import { SERVICE_TYPE_TO_FLOW } from '@/features/cases'
import { Button } from '@/components/ui/button'
import { Field, FieldContent, FieldLabel, FieldTitle, FieldError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Progress } from '@/components/ui/progress'

const CONFETTI_COLORS = ['#444CE7', '#818CF8', '#34D399', '#FDB022', '#F97066']

/** Four canned upcoming callback windows, computed as real Date objects so their ISO value is a
 * genuine `consultationSlotAt` — the label stays the same friendly copy the mock version used.
 * Tomorrow + day-after-tomorrow (not tomorrow + 3 days out) to actually match this page's own
 * "within 24-48 hours" copy.
 *
 * The label's time uses `formatTime` (locked `en-IN`/12-hour/`Asia/Kolkata`), not the device's own
 * ambient locale — `toLocaleTimeString(undefined, ...)` would otherwise render 24-hour time on
 * devices whose region setting defaults to it, while a US-region desktop browser shows 12-hour
 * AM/PM, for the exact same code. */
function buildSlotOptions() {
  const now = new Date()
  const at = (daysAhead: number, hour: number, minute: number) => {
    const d = new Date(now)
    d.setDate(d.getDate() + daysAhead)
    d.setHours(hour, minute, 0, 0)
    return d
  }
  const dayLabel = (daysAhead: number) => (daysAhead === 1 ? 'Tomorrow' : 'Day after tomorrow')
  const slots = [
    { daysAhead: 1, hour: 10, minute: 0 },
    { daysAhead: 1, hour: 14, minute: 30 },
    { daysAhead: 2, hour: 10, minute: 0 },
    { daysAhead: 2, hour: 14, minute: 30 },
  ]
  return slots.map(({ daysAhead, hour, minute }) => {
    const d = at(daysAhead, hour, minute)
    return {
      value: d.toISOString(),
      label: `${dayLabel(daysAhead)}, ${formatTime(d)}`,
    }
  })
}

function Confetti() {
  const [visible, setVisible] = useState(true)
  const pieces = useMemo(
    () =>
      Array.from({ length: 60 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.4,
        duration: 1.8 + Math.random() * 1.2,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        rotate: Math.random() * 360,
        size: 6 + Math.random() * 6,
      })),
    [],
  )

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisible(false)
      return
    }
    const t = setTimeout(() => setVisible(false), 3200)
    return () => clearTimeout(t)
  }, [])

  if (!visible) return null

  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden" aria-hidden="true">
      {pieces.map((p) => (
        <span
          key={p.id}
          style={{
            position: 'absolute',
            top: '-5%',
            left: `${p.left}%`,
            width: p.size,
            height: p.size * 0.4,
            backgroundColor: p.color,
            transform: `rotate(${p.rotate}deg)`,
            borderRadius: 2,
            animation: `confetti-fall ${p.duration}s ${p.delay}s ease-in forwards`,
          }}
        />
      ))}
    </div>
  )
}

export function SubmissionSuccessfulView() {
  const serviceType = useOnboardingStore((s) => s.serviceType)
  const document = useOnboardingStore((s) => s.document)
  const buildClientDetails = useOnboardingStore((s) => s.buildClientDetails)
  const buildExpenses = useOnboardingStore((s) => s.buildExpenses)
  const buildGoals = useOnboardingStore((s) => s.buildGoals)
  const reset = useOnboardingStore((s) => s.reset)
  const submit = useSubmitPublicCase()
  const submitBuild = useSubmitPublicBuildPortfolioCase()

  // Snapshotted once on mount (not read live off the store) — `onSubmit` calls `reset()` right
  // after a successful submission, which clears `serviceType` back to `null`. Deriving `flow`/
  // `isBuild` live off the store meant that reset flipped `isBuild` to `false` on the very next
  // render, swapping `activeSubmission` from `submitBuild` (the one that actually just succeeded)
  // to `submit` (never called for this flow) — so `activeSubmission.isSuccess` was checked against
  // the wrong mutation and the success screen never appeared, even though the case was created.
  const [initialServiceType] = useState(serviceType)
  const flow = initialServiceType ? SERVICE_TYPE_TO_FLOW[initialServiceType] : undefined
  const isBuild = flow === 'build'
  // Common surface both mutations share — `submit` also carries upload-only `progress`/
  // `isUploading` fields that `submitBuild` has no equivalent for (Build has no file), referenced
  // directly off `submit` below rather than through this union.
  const activeSubmission = isBuild ? submitBuild : submit

  const slots = useMemo(buildSlotOptions, [])

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SubmissionSuccessfulFormValues>({
    resolver: zodResolver(submissionSuccessfulSchema),
    mode: 'onTouched',
    defaultValues: { mobile: buildClientDetails?.phone ?? '', consultationSlotAt: '' },
  })

  const onSubmit = handleSubmit(async (values) => {
    if (!serviceType) return
    if (isBuild) {
      // Guarded by the wizard itself always writing all 3 before reaching this shared screen —
      // never true in practice, but if somehow reached with a gap, fail silently here rather than
      // sending a partial request the backend would just 422 anyway.
      if (!buildClientDetails || !buildExpenses || !buildGoals) return
      await submitBuild.mutateAsync({
        mobile: values.mobile,
        consultationSlotAt: values.consultationSlotAt,
        clientDetails: {
          name: buildClientDetails.name,
          age: buildClientDetails.age,
          city: buildClientDetails.city,
          email: buildClientDetails.email,
        },
        expenses: buildExpenses,
        goals: buildGoals,
      })
    } else {
      await submit.mutateAsync({
        serviceType,
        mobile: values.mobile,
        consultationSlotAt: values.consultationSlotAt,
        document: document ?? undefined,
      })
    }
    reset()
  })

  const subjectNoun =
    flow === 'trades' ? 'trading activity' : flow === 'build' ? 'plan' : 'portfolio'
  const receivedCopy =
    flow === 'trades'
      ? 'We have your trading details — a full review is on the way.'
      : flow === 'build'
        ? 'We have your starting plan — a full review is on the way.'
        : 'We found your holdings — a full review is on the way.'

  if (activeSubmission.isSuccess) {
    return (
      <OnboardingLayout eyebrow="Submitted" title="You're all set" fitContent>
        <Confetti />
        <div className="flex flex-col gap-4">
          <div className="bg-muted flex items-center gap-2.5 rounded-lg px-3.5 py-3">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-green-600/10 text-green-600">
              <Check className="size-4" />
            </span>
            <p className="text-muted-foreground text-sm">
              Reference <span className="font-semibold">{activeSubmission.data.caseReference}</span>{' '}
              — our expert will call you at your chosen time.
            </p>
          </div>
          <p className="text-muted-foreground text-xs leading-snug">
            Save this Reference ID — you&apos;ll need it with your mobile number to check your
            status anytime.
          </p>
          <Button asChild className="w-full">
            <a href={`/status/${activeSubmission.data.trackingToken}`}>Track your application</a>
          </Button>
        </div>
      </OnboardingLayout>
    )
  }

  return (
    <OnboardingLayout
      eyebrow="Almost done"
      title="One last step"
      description={`Your ${subjectNoun} details are ready. Our financial expert will call you within 24-48 hours — pick a time that works best, and we'll confirm it.`}
      footer={
        <div className="flex flex-col gap-2">
          {!isBuild && submit.isPending && <Progress value={submit.progress} className="h-1.5" />}
          {activeSubmission.isError && (
            <p className="text-destructive text-xs">
              {activeSubmission.error instanceof Error
                ? activeSubmission.error.message
                : 'Something went wrong submitting your details. Please try again.'}
            </p>
          )}
          <Button onClick={onSubmit} disabled={activeSubmission.isPending}>
            {activeSubmission.isError ? 'Try again' : 'Confirm time slot'}
          </Button>
        </div>
      }
    >
      <Confetti />

      <div className="bg-muted mb-5 flex items-center gap-2.5 rounded-lg px-3.5 py-3">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-green-600/10 text-green-600">
          <Check className="size-4" />
        </span>
        <p className="text-muted-foreground text-sm">{receivedCopy}</p>
      </div>

      <Field className="mb-5">
        <FieldLabel htmlFor="callback-mobile">Number to call you on</FieldLabel>
        <Input
          id="callback-mobile"
          type="tel"
          inputMode="numeric"
          placeholder="e.g. 9511874029"
          maxLength={10}
          aria-invalid={!!errors.mobile}
          {...register('mobile', {
            onChange: (e) => {
              e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10)
            },
          })}
        />
        <FieldError errors={[errors.mobile]} />
      </Field>

      <p className="mb-3 text-sm font-semibold">Preferred callback time</p>
      <Controller
        control={control}
        name="consultationSlotAt"
        render={({ field }) => (
          <RadioGroup
            value={field.value}
            onValueChange={field.onChange}
            className="grid grid-cols-1 gap-3 sm:grid-cols-2"
          >
            {slots.map((slot) => (
              <FieldLabel key={slot.value} htmlFor={slot.value}>
                <Field orientation="horizontal">
                  <RadioGroupItem id={slot.value} value={slot.value} />
                  <FieldContent>
                    <FieldTitle>{slot.label}</FieldTitle>
                  </FieldContent>
                </Field>
              </FieldLabel>
            ))}
          </RadioGroup>
        )}
      />
      <FieldError errors={[errors.consultationSlotAt]} />
    </OnboardingLayout>
  )
}
